import React, { useState } from 'react';
import { Trophy, Users, Lock, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './BracketTournament.css';

const BracketTournamentCreate = ({ user, onClose, onCreated, t }) => {
  // Game Mode and Map are configured per match, not for the whole tournament
  const [formData, setFormData] = useState({
    prizePool: 500,
    totalTeams: 4,
    password: '',
    roomUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const teamOptions = [
    { value: 4, label: '4 Teams (2 Rounds)', description: 'Semi-Finals → Final' },
    { value: 8, label: '8 Teams (3 Rounds)', description: 'Quarter-Finals → Semi-Finals → Final' },
    { value: 16, label: '16 Teams (4 Rounds)', description: 'Round of 16 → Quarter-Finals → Semi-Finals → Final' }
  ];

  // No fee for admins - tournament is free to create
  const prizePerWinner = Math.floor(formData.prizePool / 5);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const confirmCreate = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/bracket-tournaments/create', formData);

      toast.success(t('mpMultistepCreated', 'MP Multistep tournament created!'));

      if (onCreated) {
        onCreated(response.data.tournament, response.data.userPoints);
      }

      onClose();
    } catch (error) {
      console.error('Error creating bracket tournament:', error);
      toast.error(error.response?.data?.message || t('errorCreating', 'Error creating tournament'));
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="bracket-modal-overlay">
      <div className="bracket-modal bracket-create-modal">
        {/* Header */}
        <div className="bracket-modal-header">
          <div className="bracket-modal-title">
            <Trophy className="w-6 h-6 text-yellow-400" />
            <h2>{t('createMPMultistep', 'Create MP Multistep Tournament')}</h2>
          </div>
          <button className="bracket-modal-close" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bracket-create-form">
          {/* Info about per-match configuration */}
          <div className="bracket-info-note">
            <AlertCircle className="w-4 h-4" />
            <span>{t('gameModePerMatch', 'Game mode and map are configured for each match individually')}</span>
          </div>

          {/* Total Teams */}
          <div className="bracket-form-group">
            <label><Users className="w-4 h-4" /> {t('numberOfTeams', 'Number of Teams')}</label>
            <div className="bracket-team-options">
              {teamOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`bracket-team-option ${formData.totalTeams === option.value ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, totalTeams: option.value })}
                >
                  <span className="option-label">{option.label}</span>
                  <span className="option-desc">{option.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Prize Pool */}
          <div className="bracket-form-group">
            <label><Trophy className="w-4 h-4" /> {t('prizePool', 'Prize Pool')}</label>
            <div className="bracket-input-wrapper">
              <input
                type="number"
                min="0"
                max="1000000"
                value={formData.prizePool}
                onChange={(e) => setFormData({ ...formData, prizePool: parseInt(e.target.value) || 0 })}
              />
              <span className="input-suffix">{t('points', 'Points')}</span>
            </div>
          </div>

          {/* Password (optional) */}
          <div className="bracket-form-group">
            <label><Lock className="w-4 h-4" /> {t('password', 'Password')} ({t('optional', 'Optional')})</label>
            <input
              type="text"
              placeholder={t('leaveEmptyForPublic', 'Leave empty for public')}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>

          {/* Prize Summary - No Cost for Admins */}
          <div className="bracket-cost-summary free-admin">
            <h4>{t('prizeSummary', 'Prize Summary')}</h4>
            <div className="cost-row">
              <span>{t('prizePool', 'Prize Pool')}</span>
              <span>{formData.prizePool} {t('pts', 'pts')}</span>
            </div>
            <div className="cost-row prize">
              <span>{t('prizePerWinner', 'Prize per Winner')} (5 {t('players', 'players')})</span>
              <span>{prizePerWinner} {t('pts', 'pts')}</span>
            </div>
            <div className="cost-row free-note">
              <span>✓ {t('freeForAdmins', 'Free to create for admins')}</span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="bracket-submit-btn"
            disabled={loading}
          >
            {loading ? t('creating', 'Creating...') : t('createTournament', 'Create Tournament')}
          </button>
        </form>

        {/* Confirmation Modal */}
        {showConfirm && (
          <div className="bracket-confirm-overlay">
            <div className="bracket-confirm-modal">
              <h3>{t('confirmCreate', 'Confirm Tournament Creation')}</h3>
              <div className="confirm-details">
                <p><strong>{t('teams', 'Teams')}:</strong> {formData.totalTeams} ({formData.totalTeams * 5} {t('players', 'players')})</p>
                <p><strong>{t('prizePool', 'Prize Pool')}:</strong> {formData.prizePool} {t('pts', 'pts')}</p>
                <p><strong>{t('prizePerWinner', 'Prize per Winner')}:</strong> {prizePerWinner} {t('pts', 'pts')}</p>
                <p className="free-badge">✓ {t('freeForAdmins', 'Free to create')}</p>
              </div>
              <div className="confirm-actions">
                <button
                  className="confirm-btn confirm-yes"
                  onClick={confirmCreate}
                  disabled={loading}
                >
                  {loading ? t('creating', 'Creating...') : t('confirm', 'Confirm')}
                </button>
                <button
                  className="confirm-btn confirm-no"
                  onClick={() => setShowConfirm(false)}
                  disabled={loading}
                >
                  {t('cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BracketTournamentCreate;
